import React, { FC } from 'react';
import { Uploader } from 'rsuite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { toAbsoluteUrl } from '../../utils';
import clsx from 'clsx';
import { errorAlert } from '../ToastMessage';

interface Props {
  fileList: any[];
  setFileList: (fileList: any[]) => void;
  uploadedDocuments?: any[];
  setUploadedDocuments?: (uploadedFiles: any[]) => void;
  isError?: boolean;
  isRequired?: boolean;
  showAdditionalInfo?: boolean;
  enableToDelete?: boolean;
}

export const DocumentsUploader: FC<Props> = (props) => {
  const { t } = useTranslation();

  const {
    fileList, setFileList, uploadedDocuments,
    setUploadedDocuments, isError = false,
    isRequired, showAdditionalInfo,
    enableToDelete
  } = props;

  return (
    <>
      <div className="d-flex align-items-center mb-5">
        <div>{t('SELF_ONBOARDING.DOCUMENT_TITLE')}</div>
        {isRequired && <span className="text-danger ms-1">*</span>}
      </div>
      {
        showAdditionalInfo && (
          <>
            {/*<div className="text-muted fs-8">*/}
            {/*  Share your last 3 months of processing statements. This helps us fast-track your setup and allows us to*/}
            {/*  configure your account to maximize your approval rates.*/}
            {/*</div>*/}
            <div className="text-muted fs-8 mb-5">
              Please upload your last 3 months of processing statement, covering at least 50% of your monthly volumes.
            </div>
            {/*<div className="text-muted fs-8 mb-5">*/}
            {/*  New business with no processing history? Your Account Exec can discuss alternative paths.*/}
            {/*</div>*/}
          </>
        )
      }

      <div>
        <div className="d-flex align-items-center justify-content-between fs-8 text-muted">
          <div>Supported formats: `.pdf`, `.doc`, `.docx`, `.xls`, `.xlsx`, `.txt`, `.csv`, `.jpg`, `jpeg`, `.png`</div>
          <div>Max size: 10 MB</div>
        </div>
        <Uploader
          fileList={fileList}
          onChange={setFileList}
          action={'#'}
          autoUpload={false}
          accept=".pdf, .xls, .xlsx, .csv, .doc, .docx, .jpg, .jpeg, .png"
          className={clsx('document', isError && 'primary-border')}
          maxPreviewFileSize={5242880}
          draggable
        >
          <div>
            <FontAwesomeIcon icon={faFileArrowUp} width={16} className="h-20px mb-2" />
            <span className="fs-8">Click or Drag files to this area to upload</span>
          </div>
        </Uploader>

        <UploadedFiles
          uploadedDocuments={uploadedDocuments}
          setUploadedDocuments={setUploadedDocuments}
          enableToDelete={enableToDelete}
        />
      </div>
    </>
  );
};

interface IUploadedFiles {
  uploadedDocuments: any[];
  setUploadedDocuments?: (uploadedDocuments: any[]) => void;
  enableToDelete?: boolean;
}

const UploadedFiles: FC<IUploadedFiles> = (props) => {
  const { uploadedDocuments, setUploadedDocuments, enableToDelete } = props;

  const handleDeleteFile = (id: string): void => {
    if (uploadedDocuments && uploadedDocuments?.length > 0) {
      const updatedFiles = uploadedDocuments.filter((file) => file.id !== id);
      setUploadedDocuments && setUploadedDocuments(updatedFiles);
    }
  };

  return (
    <>
      {
        uploadedDocuments && uploadedDocuments?.length > 0 && (
          <div>
            <div className="uploaded-files">
              {
                uploadedDocuments.map(({ name, id }) => (
                  <div key={id} className="uploaded-file d-flex align-items-center justify-content-between ps-5">
                    <div className="d-flex align-items-center py-2">
                      <FontAwesomeIcon icon={faCheck} width={12} className="h-12px" />
                      <div className="file-name ms-2">{name}</div>
                      {/*<div className="file-size">{(file.size / 1024).toFixed(2)} KB</div>*/}
                    </div>
                    {
                      enableToDelete && (
                        <div className="py-2 px-4 cursor-pointer" onClick={(): void => handleDeleteFile(id)}>
                          <img
                            src={toAbsoluteUrl('/media/icons/x-mark-thin.svg')}
                            height={12}
                            width={12}
                            alt="delete-icon"
                            className="cursor-pointer uploaded-files-delete-icon"
                          />
                        </div>
                      )
                    }
                  </div>
                ))
              }
            </div>
          </div>
        )
      }
    </>
  );
};

export const checkIsFileValid = (fileList: any): boolean => {
  const maxFileSize = 10 * 1024 * 1024; // 10MB

  if (fileList?.length > 0) {
    const totalSize = fileList.reduce((sum, file: any) => sum + (file?.blobFile?.size || 0), 0);
    const largeFiles = fileList.filter((file: any) => file?.blobFile?.size >= maxFileSize);

    // Case 1: A single file is too large
    if (largeFiles.length === 1) {
      errorAlert({
        title: `File '${largeFiles[0]?.blobFile?.name}' is too large`,
        duration: 0,
        closable: true
      });
      return false;
    }

    // Case 2: Multiple large files
    if (largeFiles.length > 1) {
      const fileNames = largeFiles.map((file) => file?.blobFile?.name).join(', ');
      errorAlert({
        title: `The following files are too large: ${fileNames}`,
        duration: 0,
        closable: true
      });
      return false;
    }

    // Case 3: Total file size exceeds 10MB
    if (totalSize >= maxFileSize) {
      errorAlert({
        title: `Total file size exceeds 10MB`,
        duration: 0,
        closable: true
      });
      return false;
    }

  }

  return true;
};
